import {
  configApiRef,
  IdentityApi,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  CustomFieldExtensionSchema,
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { WorkloadClient } from '@internal/workload-api-helper';
import { Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { FieldValidation } from '@rjsf/utils';

import { useForm } from 'react-hook-form';
import { validateField } from '../helper';

export const TextFieldExtensionSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    required: ['fieldName'],
    properties: {
      fieldName: {
        description:
          'The name of the field use to validate in the accounts API',
        type: 'string',
      },
      regexPattern: {
        description: 'An optional regex to apply to the input',
        type: 'string',
      },
      regexError: {
        description:
          'An optional error message for when the regex pattern does not match',
        type: 'string',
      },
      allowedEmpty: {
        description:
          "A flag indicating if a blank value is allowed. Used to bypass backstage's redundant validation on blank fields. Defaults to false.",
        type: 'boolean',
        default: false,
      },
      formattedHelp: {
        description:
          'Help text containing HTML elements for advanced formatting',
        type: 'string',
      },
    },
  },
  returnValue: { type: 'string' },
};

let config: any;
let identity: IdentityApi;
let formContext: any;
let workloadClient: WorkloadClient;

export const TextField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  // Set up variables for accounts-api validation call
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  workloadClient = new WorkloadClient(config, oauthApi);

  formContext = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      existing_repository: 'No',
      vpc_required: 'No',
      public_subnet: 'No',
      repository_creation_enabled: 'No',
      governance_group_membership: [
        {
          value: '',
        },
        {
          value: '',
        },
      ],
    },
  });
  const fieldName = uiSchema?.['ui:options']?.fieldName as string;
  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
    >
      <InputLabel htmlFor={`${fieldName}Input`}>{schema.title}</InputLabel>
      <Input
        id={`${fieldName}Input`}
        defaultValue={formData}
        aria-describedby={`${fieldName}HelperText`}
        onChange={e => onChange(e.target?.value)}
      />
      {uiSchema?.['ui:options'] !== undefined && (
        <FormHelperText id={`${fieldName}HelperText`}>
          <Typography variant="body1">
            <div
              dangerouslySetInnerHTML={{
                __html: uiSchema['ui:options'].formattedHelp as string,
              }}
            />
          </Typography>
        </FormHelperText>
      )}
    </FormControl>
  );
};
/*
 This is a validation function that will run when the form is submitted.
  You will get the value from the `onChange` handler before as the value here to make sure that the types are aligned\
*/
export const validateTextField: CustomFieldValidator<
  string,
  {
    fieldName: string;
    regexPattern: string | undefined;
    regexError: string | undefined;
    allowedEmpty: boolean;
  }
> = async (
  value: string,
  validation: FieldValidation,
  { uiSchema },
): Promise<void> => {
  if (!uiSchema) throw Error('No uiSchema provided!');

  const uiOptions = uiSchema?.['ui:options'];
  if (!uiOptions) throw Error('No ui:options property in uiSchema!');

  if (!value && uiOptions.allowedEmpty) return;

  if (uiOptions.regexPattern) {
    const regex = RegExp(uiOptions.regexPattern);
    if (!regex.test(value)) {
      validation.addError(
        uiOptions.regexError
          ? uiOptions.regexError
          : `Please enter a value matching the regex pattern "${uiOptions.regexPattern}"`,
      );
      return;
    }
  }
  const apiValidation = {
    form: formContext,
    config: config,
    identity: identity,
    workloadClient: workloadClient,
  };
  const errorMessage = await validateField(
    uiOptions.fieldName,
    value,
    apiValidation,
  );
  if (errorMessage) validation.addError(errorMessage);
};
